// /admin/v1/content/* — content packages, prompt templates, categories.

import { del, get, list, patch, post } from './client';
import type { ListResult } from './client';

export type PackageStatus = 'draft' | 'reviewing' | 'active' | 'archived';

export interface ContentPackage {
  id: number;
  app_id: number;
  code: string;
  name: string;
  cover_url?: string | null;
  workflow_code?: string;
  prompt_template_id?: number;
  lora_model_id?: number;
  current_version?: number;
  status: PackageStatus;
  price_cents?: number;
  diamond_cost?: number;
  audience_tags?: Record<string, unknown>;
  config?: Record<string, unknown>;
  sales_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface ListPackagesQuery {
  q?: string;
  app_id?: number;
  status?: PackageStatus;
  /** category slug 过滤 (来自 package_categories.slug) */
  category?: string;
  page?: number;
  page_size?: number;
}

export function listPackages(
  q: ListPackagesQuery = {},
): Promise<ListResult<ContentPackage>> {
  return list<ContentPackage>('/admin/v1/content/packages', q);
}

// --- categories ----------------------------------------------------------------

export interface PackageCategory {
  id: number;
  app_id: number;
  parent_id?: number | null;
  name: string;
  slug: string;
  sort_order?: number;
  enabled?: boolean;
  created_at?: string;
  updated_at?: string;
}

/** 拉指定 App 的全量套系分类列表 (后台用) */
export function listCategories(appId: number): Promise<PackageCategory[]> {
  return get<PackageCategory[]>(`/admin/v1/content/categories?app_id=${appId}`);
}

export function getPackage(id: number): Promise<ContentPackage> {
  return get<ContentPackage>(`/admin/v1/content/packages/${id}`);
}

export interface UpsertPackageInput {
  app_id?: number;
  code?: string;
  name?: string;
  cover_url?: string;
  workflow_code?: string;
  prompt_template_id?: number;
  price_cents?: number;
  diamond_cost?: number;
  audience_tags?: Record<string, unknown>;
  config?: Record<string, unknown>;
}

export function createPackage(input: UpsertPackageInput): Promise<ContentPackage> {
  return post<ContentPackage>('/admin/v1/content/packages', input);
}

// --- series-bulk-import: 批量 upsert 套系 (幂等 app_id+code) -----------------

export interface BulkImportSample {
  url: string;
  sort_order?: number;
  body_type?: string;
}

export interface BulkImportItem {
  code: string;
  name: string;
  cover_url?: string;
  workflow_code: string;
  prompt_template_id?: number;
  /** 套系画面 prompt (英文); 提供且无 prompt_template_id 时导入自动建模板并挂上 — 写真/series 流无需答题即可出图。 */
  image_prompt?: string;
  price_cents?: number;
  diamond_cost?: number;
  status?: PackageStatus;
  samples?: BulkImportSample[];
}

export interface BulkImportInput {
  app_id: number;
  items: BulkImportItem[];
}

export interface BulkImportItemResult {
  code: string;
  action: 'created' | 'updated' | 'failed';
  package_id?: number;
  error?: string;
}

export interface BulkImportResult {
  created: number;
  updated: number;
  failed: number;
  outcomes: BulkImportItemResult[];
}

export function bulkImportPackages(input: BulkImportInput): Promise<BulkImportResult> {
  return post<BulkImportResult>('/admin/v1/content/packages/bulk-import', input);
}

export function updatePackage(id: number, input: UpsertPackageInput): Promise<ContentPackage> {
  return patch<ContentPackage>(`/admin/v1/content/packages/${id}`, input);
}

export function setPackageStatus(id: number, status: PackageStatus): Promise<ContentPackage> {
  return post<ContentPackage>(`/admin/v1/content/packages/${id}/status`, { status });
}

export function archivePackage(id: number): Promise<ContentPackage> {
  return post<ContentPackage>(`/admin/v1/content/packages/${id}/archive`);
}

export interface PackageSample {
  id: number;
  package_id: number;
  url: string;
  sort_order?: number;
  body_type?: string;
  created_at?: string;
}

// listSamples 列出套系全部底版/样片 (后端返回已签名的可读 URL)。
export function listSamples(id: number): Promise<PackageSample[]> {
  return get<PackageSample[]>(`/admin/v1/content/packages/${id}/samples`);
}

export function uploadSamples(
  id: number,
  items: Array<{ url: string; sort_order?: number; body_type?: string }>,
): Promise<ListResult<PackageSample>> {
  return list<PackageSample>(`/admin/v1/content/packages/${id}/samples`).then(async () => {
    // POST returns the newly inserted batch; reuse list<> shape for the response.
    const created = await post<{ data: PackageSample[] } | PackageSample[]>(
      `/admin/v1/content/packages/${id}/samples`,
      { items },
    );
    const arr = Array.isArray(created) ? created : created.data;
    return { data: arr };
  });
}

export function deleteSample(sampleId: number): Promise<void> {
  return del(`/admin/v1/content/samples/${sampleId}`);
}

// --- prompt templates ------------------------------------------------------

export interface PromptTemplate {
  id: number;
  code: string;
  name: string;
  current_version?: number;
  variables?: Array<{ key: string; type: string; required?: boolean }>;
  created_at?: string;
  updated_at?: string;
}

export function listPromptTemplates(): Promise<ListResult<PromptTemplate>> {
  return list<PromptTemplate>('/admin/v1/content/prompt-templates');
}
